import "@/App.css";
import "@/lib/i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { PresenceProvider } from "@/contexts/PresenceContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { PatientContextProvider } from "@/contexts/PatientContext";
import { FocusModeProvider } from "@/contexts/FocusModeContext";
import GlobalVoiceCommands from "@/components/GlobalVoiceCommands";
import PremiumVoiceAssistant from "@/components/PremiumVoiceAssistant";
import PremiumTranslator from "@/components/PremiumTranslator";
import CommandPaletteButton from "@/components/CommandPaletteButton";

// HOS Migration utilities
import { ROUTE_MIGRATION_MAP } from "@/utils/hosMigration";

// Pages
import Landing from "@/pages/Landing";
import Chat from "@/pages/Chat";
import HospiceEducation from "@/pages/HospiceEducation";
import SymptomGuide from "@/pages/SymptomGuide";
import CaregiverResources from "@/pages/CaregiverResources";
import CaregiverJournal from "@/pages/CaregiverJournal";
import TrainingVideos from "@/pages/TrainingVideos";
import VisitPrep from "@/pages/VisitPrep";
import Messages from "@/pages/Messages";
import Contact from "@/pages/Contact";
import AdminDashboard from "@/pages/AdminDashboard";
import ProviderPortal from "@/pages/ProviderPortal";
import Resources from "@/pages/Resources";
import StaffDashboard from "@/pages/StaffDashboard";
import Programs from "@/pages/Programs";
import JoyfulMemories from "@/pages/JoyfulMemories";
import ComplianceDashboard from "@/pages/ComplianceDashboard";
import CareDashboard from "@/pages/CareDashboard";
import MedicationManagement from "@/pages/MedicationManagement";
import CareCalendar from "@/pages/CareCalendar";

// Facility Portal Pages
import FacilityAuth from "@/pages/FacilityAuth";
import FacilityDashboard from "@/pages/FacilityDashboard";
import HospiceCriteria from "@/pages/HospiceCriteria";
import PatientReferralForm from "@/pages/PatientReferralForm";

// Login Page
import LoginPage from "@/pages/LoginPage";

// Physician Portal Pages
import PhysicianAuth from "@/pages/PhysicianAuth";
// PhysicianDashboard and PhysicianDashboardEnhanced archived - now using PhysicianCommandCenter
import PhysicianCommandCenter from "@/pages/PhysicianCommandCenter";
import RNCMDashboardPage from "@/pages/RNCMDashboardPage";

// Staff Pages
import QualityImprovement from "@/pages/QualityImprovement";
import HospiceAcademy from "@/pages/HospiceAcademy";
import AdminSuperDashboard from "@/pages/AdminSuperDashboard";
import StaffPortal from "@/pages/StaffPortal";
import StaffRoster from "@/pages/StaffRoster";
import PartnerPortal from "@/pages/PartnerPortal";
import PhysicianPortalMock from "@/pages/PhysicianPortalMock";
import TimeOfDeath from "@/pages/TimeOfDeath";

// Registration Pages
import PartnerRegistration from "@/pages/PartnerRegistration";
import PatientFamilyRegistration from "@/pages/PatientFamilyRegistration";
import PartnerDashboard from "@/pages/PartnerDashboard";

// 404 Not Found Page
import NotFoundPage from "@/pages/NotFoundPage";
import CEUDashboard from "@/pages/CEUDashboard";
import SystemHealth from "@/pages/SystemHealth";
import ReferralDashboard from "@/pages/ReferralDashboard";
import AdminApprovalDashboard from "@/pages/AdminApprovalDashboard";
import AdmissionsDashboard from "@/pages/AdmissionsDashboard";
import OrderManagement from "@/pages/OrderManagement";
import Telehealth from "@/pages/Telehealth";
import ResetPassword from "@/pages/ResetPassword";
import PatientMessaging from "@/pages/PatientMessaging";
import AdminActivityDashboard from "@/pages/AdminActivityDashboard";
import CallTriage from "@/pages/CallTriage";
import EHRDashboard from "@/pages/EHRDashboard";
import EnhancedFeatures from "@/pages/EnhancedFeatures";
import Phase2Features from "@/pages/Phase2Features";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import SmartScheduling from "@/pages/SmartScheduling";
import CEUCertificates from "@/pages/CEUCertificates";
import DemoPage from "@/pages/DemoPage";
import DemoGuidePage from "@/pages/DemoGuidePage";
import ExecutiveDemoGuide from "@/pages/ExecutiveDemoGuide";
import VideoLibrary from "@/pages/VideoLibrary";
import RealTimeOperationsDashboard from "@/pages/RealTimeOperationsDashboard";
import PredictiveAnalyticsDashboard from "@/pages/PredictiveAnalyticsDashboard";
import QualityMetricsDashboard from "@/pages/QualityMetricsDashboard";
import HIPAADashboard from "@/pages/HIPAADashboard";
import VideoAnalyticsDashboard from "@/pages/VideoAnalyticsDashboard";
import CommandCenter from "@/pages/CommandCenter";
import FamilyHub from "@/pages/FamilyHub";
import PatientOutcomesDashboard from "@/pages/PatientOutcomesDashboard";
import HospiceEHRDashboard from "@/pages/HospiceEHRDashboard";
import VoiceDocumentation from "@/pages/VoiceDocumentation";
import ClinicalIntelligence from "@/pages/ClinicalIntelligence";
import EHRCommandCenter from "@/pages/EHRCommandCenter";
import DocumentManagement from "@/pages/DocumentManagement";
import ReferralToEMR from "@/pages/ReferralToEMR";
import ComplianceManagement from "@/pages/ComplianceManagement";
import EHRMedicationOrders from "@/pages/EHRMedicationOrders";
import PredictiveAnalytics from "@/pages/PredictiveAnalytics";
import EHRCompliancePage from "@/pages/EHRCompliancePage";
import EHRReferralIntakePage from "@/pages/EHRReferralIntakePage";
import EHROrdersMedicationsPage from "@/pages/EHROrdersMedicationsPage";
import EHRIDGMeetingPage from "@/pages/EHRIDGMeetingPage";
import EHRDischargeRevocationPage from "@/pages/EHRDischargeRevocationPage";
import StaffVisitsPage from "@/pages/StaffVisitsPage";
import AICarePlansPage from "@/pages/AICarePlansPage";
import DocumentationQAPage from "@/pages/DocumentationQAPage";
import CTIGraderPage from "@/pages/CTIGraderPage";
import AISchedulingPage from "@/pages/AISchedulingPage";
import OMSAutomationPage from "@/pages/OMSAutomationPage";
import FallAutomationPage from "@/pages/FallAutomationPage";
import QAPIFallLogPage from "@/pages/QAPIFallLogPage";
import PredictiveFallsPage from "@/pages/PredictiveFallsPage";
import SkinAutomationPage from "@/pages/SkinAutomationPage";
import RespiratoryCrisisPage from "@/pages/RespiratoryCrisisPage";
import FamilyPortalPage from "@/pages/FamilyPortalPage";
import IDGSummaryPage from "@/pages/IDGSummaryPage";
import HospiceOSPage from "@/pages/HospiceOSPageWrapper";
import StaffToolsPage from "@/pages/StaffToolsPage";
import VisitFrequencyPage from "@/pages/VisitFrequencyPage";
import PatientPortalPage from "@/pages/PatientPortalPage";
import NotificationCenterPage from "@/pages/NotificationCenterPage";
import EmailAnalyticsPage from "@/pages/EmailAnalyticsPage";
import TranslationExportPage from "@/pages/TranslationExportPage";
import SettingTransitions from "@/pages/SettingTransitions";
import VolunteerBereavement from "@/pages/VolunteerBereavement";
import VolunteerManagement from "@/pages/VolunteerManagement";
import SchedulingManagement from "@/pages/SchedulingManagement";
import InteroperabilityHub from "@/pages/InteroperabilityHub";
import RegulatoryCompliance from "@/pages/RegulatoryCompliance";
import RCMManagement from "@/pages/RCMManagement";
import OperationsManagement from "@/pages/OperationsManagement";

// Demo Generator Page
import DemoGeneratorPage from "@/pages/DemoGeneratorPage";

// Clinical Automation Dashboard (Master System Role)
import ClinicalAutomationDashboard from "@/components/ClinicalAutomationDashboard";

// Compliance Hub (P0 Critical Features - HOPE, F2F, Med Rec, Teach-back)
import ComplianceHub from "@/components/ComplianceHub";

// Protected Route Component
import ProtectedRoute from "@/components/ProtectedRoute";
import ClaimsScrubbing from "@/pages/ClaimsScrubbing";
import PeerReviewManagement from "@/pages/PeerReviewManagement";
import FinancialReports from "@/pages/FinancialReports";
import ReportsPage from "@/pages/ReportsPage";
import VideoCallCenter from "@/pages/VideoCallCenter";
import ChatCenter from "@/pages/ChatCenter";
import AIAnalyticsDashboard from "@/pages/AIAnalyticsDashboard";
import ClinicalAIDashboard from "@/pages/ClinicalAIDashboard";
import OperationsDashboard from "@/pages/OperationsDashboard";
import PerformanceBIDashboard from "@/pages/PerformanceBIDashboard";
import IntegrationsDashboard from "@/pages/IntegrationsDashboard";
import HotlistDashboard from "@/pages/HotlistDashboard";
import VisitRequestsDashboard from "@/pages/VisitRequestsDashboard";
import CustomAlertsDashboard from "@/pages/CustomAlertsDashboard";
import FamilyPortalDashboard from "@/pages/FamilyPortalDashboard";
import SBARPromptTool from "@/pages/SBARPromptTool";
import AIEPrescriptionDashboard from "@/pages/AIEPrescriptionDashboard";
import ReferralAdmissionDashboard from "@/pages/ReferralAdmissionDashboard";
import IntegratedReferralPortal from "@/pages/IntegratedReferralPortal";
import PhysicianMessagingCenter from "@/pages/PhysicianMessagingCenter";
import DMESuppliesOrdering from "@/pages/DMESuppliesOrdering";
import StaffSuppliesPortal from "@/pages/StaffSuppliesPortal";
import PhysicianAIDashboard from "@/pages/PhysicianAIDashboard";
import RNAIDashboard from "@/pages/RNAIDashboard";
import AdmissionWorkflowTracker from "@/pages/AdmissionWorkflowTracker";
import EscalationsDashboard from "@/pages/EscalationsDashboard";
import HOPEDashboard from "@/pages/HOPEDashboard";
import FamilySymptomPortal from "@/pages/FamilySymptomPortal";
import IDGLiveMeeting from "@/pages/IDGLiveMeeting";

// AI-Automated Medication Workflow
import MedicationWorkflowPage from "@/pages/MedicationWorkflowPage";

// Unified Command Center - Landing Page After Sign-In
import UnifiedCommandCenter from "@/pages/UnifiedCommandCenter";

// AI Patient Suggestions - Full Screen Mode
import AIPatientSuggestionsFullScreen from "@/pages/AIPatientSuggestionsFullScreen";

// Style Guide Page
import StyleGuidePage from "@/pages/StyleGuidePage";

// New Enhanced Components
import AccessibilitySettings from "@/components/AccessibilitySettings";
import LegacyMemoriesJournal from "@/components/LegacyMemoriesJournal";
import SymptomDiary from "@/components/SymptomDiary";
import AppointmentSelfScheduler from "@/components/AppointmentSelfScheduler";
import ExecutiveDashboard from "@/components/ExecutiveDashboard";

// Phase 24 Components (Gamification, Timeline, Surveys, Volunteer)
import GamificationDashboard from "@/components/GamificationDashboard";
import CareTimeline from "@/components/CareTimeline";
import SatisfactionSurvey, { SurveyAnalytics } from "@/components/SatisfactionSurvey";
import VolunteerPortal from "@/components/VolunteerPortal";

// Phase 25 Components (Team Chat, Report Builder)
import TeamChat from "@/components/TeamChat";
import ReportBuilder from "@/components/ReportBuilder";

// Phase 26 Components (Wellness, Recognition)
import StaffWellness from "@/components/StaffWellness";
import PeerRecognition from "@/components/PeerRecognition";

// Phase 27 Pages (Telehealth, Advanced Analytics, Document Management)
import TelehealthCenterPage from "@/pages/TelehealthCenterPage";
import AdvancedAnalyticsPage from "@/pages/AdvancedAnalyticsPage";
import DocumentCenterPage from "@/pages/DocumentCenterPage";

// Phase 28 Pages (PWA Offline, Push Notifications)
import OfflineSyncPage from "@/pages/OfflineSyncPage";

// Phase 29 Pages (FHIR Interoperability, EMR Integration)
import InteroperabilityHubPage from "@/pages/InteroperabilityHubPage";

// Phase 30 Pages (Clinical Alerts, Billing RCM)
import ClinicalAlertsPage from "@/pages/ClinicalAlertsPage";
import BillingPage from "@/pages/BillingPage";

// Phase 31 Pages (Clinical Decision Support, DME Integration)
import ClinicalDecisionSupportPage from "@/pages/ClinicalDecisionSupportPage";
import DMEPage from "@/pages/DMEPage";

// Phase 32 Pages (Enhanced Volunteer Portal)
import VolunteerEnhancedPage from "@/pages/VolunteerEnhancedPage";

// Phase 33 Pages (Predictive AI Dashboard)
import PredictiveAIDashboardPage from "@/pages/PredictiveAIDashboardPage";

// Phase 34 Pages (Telehealth Video Module)
import TelehealthVideoPage from "@/pages/TelehealthVideoPage";

// Phase 35 Pages (Portal Enhancements)
import PendingApprovalPage from "@/pages/PendingApprovalPage";
import PhysicianPortalEnhancedPage from "@/pages/PhysicianPortalEnhancedPage";
import PatientDashboardPage from "@/pages/PatientDashboardPage";
import ProfilePage from "@/pages/ProfilePage";
import NotificationsPage from "@/pages/NotificationsPage";

// Phase 36 Pages (Hospice Eligibility & Family Portal)
import HospiceEligibilityPage from "@/pages/HospiceEligibilityPage";
import FamilyPortalEnhancedPage from "@/pages/FamilyPortalEnhancedPage";

// Phase 38-44 Components (Comprehensive Platform Overhaul)
import CaregiverCommandCenter from "@/components/CaregiverCommandCenter";
import CollaboratingPhysicianPortal from "@/components/CollaboratingPhysicianPortal";
import AdvancedTelehealthInterface from "@/components/AdvancedTelehealthInterface";
import StabilityScoreDashboard from "@/components/StabilityScoreDashboard";
import FacilityTransparencyPortal from "@/components/FacilityTransparencyPortal";
import LegacyBereavementModule from "@/components/LegacyBereavementModule";
import SmartTriageDashboard from "@/components/SmartTriageDashboard";

// Phase 45 Components (Additional Enhancements)
import CaregiverConfidenceAnalytics from "@/components/CaregiverConfidenceAnalytics";
import ResourceLibrary from "@/components/ResourceLibrary";
import BusinessDocumentsPage from "@/pages/BusinessDocumentsPage";
import Wave1FeaturesPage from "@/pages/Wave1FeaturesPage";
import Wave2FeaturesPage from "@/pages/Wave2FeaturesPage";
import Wave3FeaturesPage from "@/pages/Wave3FeaturesPage";
import Wave4FeaturesPage from "@/pages/Wave4FeaturesPage";
import AmbientDocumentationPage from "@/pages/AmbientDocumentationPage";
import DemoLandingPage from "@/pages/DemoLandingPage";
import IntelligenceDashboardPage from "@/pages/IntelligenceDashboardPage";
import VideoVisitPage from "@/pages/VideoVisitPage";
import PatientChartPage from "@/pages/PatientChartPage";
import WorkflowBillingPage from "@/pages/WorkflowBillingPage";
import AIScribe from "@/components/AIScribe";
import UnifiedTimeline from "@/components/UnifiedTimeline";
import SmartOrderSet from "@/components/SmartOrderSet";
import GeofencingDashboard from "@/components/GeofencingDashboard";

// Phase 46 Components (Peer Support & Offline)
import CaregiverPeerSupport from "@/components/CaregiverPeerSupport";
import MedicationReminders from "@/components/MedicationReminders";
import CarePlanExport from "@/components/CarePlanExport";
import LegacyVideoMessages from "@/components/LegacyVideoMessages";

// Phase 47 Components (PWA, Scheduling, Executive Tools)
import AppointmentScheduler from "@/components/AppointmentScheduler";
import SymptomDiaryEnhanced from "@/components/SymptomDiaryEnhanced";
import ExecutiveDashboardEnhanced from "@/components/ExecutiveDashboardEnhanced";
import IncidentReporting from "@/components/IncidentReporting";

// Phase 48 Components (Grief & Bereavement Support)
import GriefSupport from "@/components/GriefSupport";

// Phase 49 Components (Virtual Tours, Benchmarks, Care Planning)
import VirtualTours from "@/components/VirtualTours";
import BenchmarkAnalytics from "@/components/BenchmarkAnalytics";

// Phase 50 Components (Workforce Management)
import WorkforceManagement from "@/components/WorkforceManagement";

// Phase 51 Components (Volunteer Management) - Using enhanced page version
// Original component at @/components/VolunteerManagement preserved for backwards compatibility
import VolunteerManagementLegacy from "@/components/VolunteerManagement";

// Phase 52 Components (GPS & Smart Watch)
import GPSSmartWatch from "@/components/GPSSmartWatch";

// Phase 53 Components (Role Permission Builder & Workflow Automation)
import RolePermissionBuilder from "@/components/RolePermissionBuilder";

// Phase 54 Components (Audit Trail & Integration Dashboard)
import AuditTrailExplorer from "@/components/AuditTrailExplorer";

// Phase 55 Components (Video Conferencing)
import VideoConference from "@/components/VideoConference";

// Phase 56 Components (Billing & RCM)
import BillingRCM from "@/components/BillingRCM";

// Phase 57 Components (DME & Pharmacy Enhanced)
import DMEPharmacyDashboard from "@/components/DMEPharmacyDashboard";

// Phase 58 Components (Quality & Compliance)
import QualityComplianceDashboard from "@/components/QualityComplianceDashboard";

// Phase 59 Components (Real-Time Notifications)
import NotificationCenter from "@/components/NotificationCenter";

// Phase 60 Components (Medication Interaction Checker)
import MedicationInteractionChecker from "@/components/MedicationInteractionChecker";

// Phase 61 Components (Auto-Reorder)
import AutoReorderDashboard from "@/components/AutoReorderDashboard";

// Phase 62 Components (Bereavement Support)
import BereavementSupportDashboard from "@/components/BereavementSupportDashboard";

// Phase 63 Components (Patient Acuity Scoring)
import PatientAcuityDashboard from "@/components/PatientAcuityDashboard";
import PatientAcuityTrendsDashboard from "@/components/PatientAcuityTrendsDashboard";
import PredictiveEscalationAlerts from "@/components/PredictiveEscalationAlerts";

// Phase 64 Components (Voice-to-Documentation)
import VoiceDocumentationDashboard from "@/components/VoiceDocumentationDashboard";

// RN Voice Command Agent (Voice-First Clinical Documentation)
import RNVoiceCommand from "@/components/RNVoiceCommand";

// RN Command Center (Comprehensive RN Dashboard)
import RNCommandCenter from "@/pages/RNCommandCenter";

// HOPE Demographics Page (Tier 3 Compliance)
import HOPEDemographicsPage from "@/pages/HOPEDemographicsPage";
// PhysicianCTIPage archived - functionality now in PhysicianCommandCenter

// Admin Settings Page (Scheduled Reports)
import AdminSettingsPage from "@/pages/AdminSettingsPage";

// Phase 157 Components (LCD Eligibility Checker)
import LCDEligibilityChecker from "@/pages/LCDEligibilityChecker";
import EHRInteropConfig from "@/pages/EHRInteropConfig";

// Phase 65 Components (Smart Scheduling)
import SmartSchedulingDashboard from "@/components/SmartSchedulingDashboard";

// Phase 66 Components (Predictive Escalation)
import PredictiveEscalationDashboard from "@/components/PredictiveEscalationDashboard";

// Phase 67 Components (Family Portal Enhanced)
import FamilyPortalEnhanced from "@/components/FamilyPortalEnhanced";

// Phase 68 Components (Referral Source Portal)
import ReferralSourcePortal from "@/components/ReferralSourcePortal";

// Phase 69 Components (Staff Performance Analytics)
import StaffPerformanceDashboard from "@/components/StaffPerformanceDashboard";

// Phase 70 Components (Family Caregiver Mobile App - PWA)
import CaregiverMobileApp from "@/components/CaregiverMobileApp";

// Phase 71 Components (VR Comfort Experiences)
import VRComfortDashboard from "@/components/VRComfortDashboard";

// Phase 72 Components (AI Grief Counselor)
import GriefCounselorChat from "@/components/GriefCounselorChat";

// Phase 73 Components (Daily Wellness Check-in)
import WellnessCheckin from "@/components/WellnessCheckin";

// Phase 74 Components (Memory & Legacy Journal)
import MemoryJournal from "@/components/MemoryJournal";

// Phase 75 Components (Enhanced Caregiver Features)
import CrisisHeader from "@/components/CrisisHeader";
import CaregiverDashboard from "@/components/CaregiverDashboard";
import LocalResourceFilter from "@/components/LocalResourceFilter";

// Phase 76 & 77 Components (Analytics & Integrations)
import AdvancedAnalyticsDashboard from "@/components/AnalyticsDashboard";
import IntegrationMarketplace from "@/components/IntegrationMarketplace";

// Phase 78 Components (Push Notifications & Combined Registration)
import CombinedRegistration from "@/components/CombinedRegistration";
import PushNotificationManager from "@/components/PushNotificationManager";

// Phase 79 Components (Family Photo Gallery)
import FamilyPhotoGallery from "@/components/FamilyPhotoGallery";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LanguageTour from "@/components/LanguageTour";
import GeoLanguagePrompt from "@/components/GeoLanguagePrompt";
import TranslationDashboard from "@/components/TranslationDashboard";

// Authenticated Command Palette - Only shows for logged-in staff/admin
// Does NOT show on /hos page since HOS has its own command palette
function AuthenticatedCommandPalette() {
  const { user } = require("@/contexts/AuthContext").useAuth();
  const location = require("react-router-dom").useLocation();
  
  const isStaffOrAdmin = user?.role && ["staff", "admin", "administrator", "nurse", "doctor", "physician", "coordinator", "medical_director", "social_worker", "chaplain", "provider"].includes(user.role);
  
  // Don't show global command palette on HOS page (it has its own)
  const isHOSPage = location.pathname.startsWith("/hos");
  
  if (!isStaffOrAdmin || isHOSPage) return null;
  
  return (
    <CommandPaletteButton 
      variant="floating" 
      viewContext="general"
      userRole={user?.role || "staff"}
      showKeyboardHint={true}
    />
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PresenceProvider>
          <PatientContextProvider>
            <FocusModeProvider>
            <div className="App min-h-screen flex flex-col">
              <BrowserRouter>
                <CrisisHeader />
                <Navbar />
                <LanguageTour />
                <GeoLanguagePrompt />
                {/* Command Palette - Only visible for authenticated staff/admin */}
                <AuthenticatedCommandPalette />
                <main className="flex-1">
                  <Routes>
                    <Route path="/" element={<Landing />} />
                  <Route path="/demo" element={<DemoLandingPage />} />
                  <Route path="/demo-guide" element={<DemoGuidePage />} />
                  <Route path="/demo-generator" element={<DemoGeneratorPage />} />
                  <Route path="/executive-demo" element={<ExecutiveDemoGuide />} />
                <Route path="/video-library" element={<VideoLibrary />} />
                <Route path="/chat" element={<Chat />} />
                <Route path="/education" element={<HospiceEducation />} />
                <Route path="/symptoms" element={<SymptomGuide />} />
                <Route path="/caregiver" element={<CaregiverResources />} />
                <Route path="/journal" element={<CaregiverJournal />} />
                <Route path="/videos" element={<TrainingVideos />} />
                <Route path="/visit-prep" element={<VisitPrep />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/translation-dashboard" element={<TranslationDashboard />} />
                <Route path="/provider" element={<ProviderPortal />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/staff" element={<StaffDashboard />} />
                <Route path="/programs" element={<Programs />} />
                <Route path="/memories" element={<JoyfulMemories />} />
                <Route path="/compliance" element={<ComplianceDashboard />} />
                <Route path="/dashboard" element={<CareDashboard />} />
                <Route path="/medications" element={<MedicationManagement />} />
                <Route path="/calendar" element={<CareCalendar />} />
                
                {/* Unified Command Center - Staff/Admin Landing Page */}
                <Route path="/command-center" element={<UnifiedCommandCenter />} />
                
                {/* AI Patient Suggestions - Full Screen Mode */}
                <Route path="/patient/:patientId/ai-suggestions" element={<AIPatientSuggestionsFullScreen />} />
                <Route path="/ai-suggestions" element={<AIPatientSuggestionsFullScreen />} />
                
                {/* Settings redirect to Profile */}
                <Route path="/settings" element={<Navigate to="/profile" replace />} />
                
                {/* New Enhanced Feature Routes */}
                <Route path="/settings/accessibility" element={<AccessibilitySettings />} />
                <Route path="/legacy-memories" element={<LegacyMemoriesJournal />} />
                <Route path="/symptom-diary" element={<SymptomDiary />} />
                <Route path="/schedule-appointment" element={<AppointmentSelfScheduler />} />
                <Route path="/executive-dashboard" element={<ExecutiveDashboard />} />
                <Route path="/style-guide" element={<StyleGuidePage />} />
                
                {/* Phase 24 Enhancement Routes */}
                <Route path="/gamification" element={<GamificationDashboard userId="current_user" />} />
                <Route path="/care-timeline/:patientId" element={<CareTimeline />} />
                <Route path="/survey/:invitationId" element={<SatisfactionSurvey />} />
                <Route path="/survey-analytics" element={<SurveyAnalytics />} />
                <Route path="/volunteer-portal" element={<VolunteerPortal />} />
                
                {/* Phase 25 Enhancement Routes */}
                <Route path="/team-chat" element={<TeamChat />} />
                <Route path="/reports" element={<ReportBuilder />} />
                
                {/* Phase 26 Enhancement Routes */}
                <Route path="/wellness" element={<StaffWellness />} />
                <Route path="/recognition" element={<PeerRecognition />} />
                
                {/* Phase 27 Enhancement Routes */}
                <Route path="/telehealth-center" element={<TelehealthCenterPage />} />
                <Route path="/advanced-analytics" element={<AdvancedAnalyticsPage />} />
                <Route path="/document-center" element={<DocumentCenterPage />} />
                <Route path="/document-center/:patientId" element={<DocumentCenterPage />} />
                
                {/* Phase 28 Enhancement Routes (PWA/Offline) */}
                <Route path="/offline-settings" element={<OfflineSyncPage />} />
                
                {/* Phase 29 Enhancement Routes (Interoperability) */}
                <Route path="/interoperability" element={<InteroperabilityHubPage />} />
                
                {/* Phase 30 Enhancement Routes (Alerts & Billing) */}
                <Route path="/clinical-alerts" element={<ClinicalAlertsPage />} />
                <Route path="/billing" element={<BillingPage />} />
                
                {/* Phase 31 Enhancement Routes (Clinical Decision Support & DME) */}
                <Route path="/clinical-decision-support" element={<ClinicalDecisionSupportPage />} />
                <Route path="/dme" element={<DMEPage />} />
                
                {/* Phase 32 Enhancement Routes (Enhanced Volunteer Portal) */}
                <Route path="/volunteer-enhanced" element={<VolunteerEnhancedPage />} />
                
                {/* Phase 33 Enhancement Routes (Predictive AI Dashboard) */}
                <Route path="/predictive-ai" element={<PredictiveAIDashboardPage />} />
                
                {/* Phase 34 Enhancement Routes (Telehealth Video Module) */}
                <Route path="/telehealth-video" element={<TelehealthVideoPage />} />
                
                {/* Phase 35 Enhancement Routes (Portal Enhancements) */}
                <Route path="/pending-approval" element={<PendingApprovalPage />} />
                <Route path="/physician-portal-enhanced" element={<PhysicianPortalEnhancedPage />} />
                <Route path="/patient-dashboard" element={<PatientDashboardPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                
                {/* Phase 36 Enhancement Routes (Hospice Eligibility & Family Portal) */}
                <Route path="/hospice-eligibility" element={<HospiceEligibilityPage />} />
                <Route path="/family-portal" element={<FamilyPortalEnhancedPage />} />
                
                {/* Login Page */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signin" element={<LoginPage />} />
                
                {/* Facility Portal Routes */}
                <Route path="/facility" element={<FacilityAuth />} />
                <Route path="/facility/dashboard" element={<FacilityDashboard />} />
                <Route path="/facility/criteria" element={<HospiceCriteria />} />
                <Route path="/facility/referral/new" element={<PatientReferralForm />} />
                {/* Physician Portal Routes */}
                <Route path="/physician" element={<PhysicianAuth />} />
                <Route path="/physician/dashboard" element={<Navigate to="/physician-command-center" replace />} />
                <Route path="/physician-command-center" element={<PhysicianCommandCenter />} />
                {/* Staff Routes */}
                <Route path="/quality" element={<QualityImprovement />} />
              <Route path="/academy" element={<HospiceAcademy />} />
              <Route path="/ceu-dashboard" element={<CEUDashboard />} />
              <Route path="/system-health" element={<SystemHealth />} />
              <Route path="/referrals" element={<ReferralDashboard />} />
              <Route path="/approvals" element={<AdminApprovalDashboard />} />
              <Route path="/orders" element={<OrderManagement />} />
              <Route path="/telehealth" element={<Telehealth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/command" element={<Navigate to="/hos" replace />} />
              <Route path="/staff-portal" element={<StaffPortal />} />
              <Route path="/staff-roster" element={<Navigate to="/hos?view=staff-roster" replace />} />
              <Route path="/partner-portal" element={<PartnerPortal />} />
              <Route path="/physician-portal" element={<PhysicianPortalMock />} />
              <Route path="/time-of-death" element={<TimeOfDeath />} />
              <Route path="/patient-messages" element={<PatientMessaging />} />
              <Route path="/admin/activities" element={<Navigate to="/hos?view=activities" replace />} />
              <Route path="/admin/triage" element={<Navigate to="/hos?view=triage" replace />} />
              <Route path="/admin/ehr" element={<Navigate to="/hos" replace />} />
              <Route path="/admin/ai-features" element={<Navigate to="/hos?view=ai-features" replace />} />
              <Route path="/admin/clinical-intelligence" element={<Navigate to="/hos?view=clinical-intelligence" replace />} />
              <Route path="/admin/analytics" element={<Navigate to="/hos?view=analytics" replace />} />
              <Route path="/admin/scheduling" element={<Navigate to="/hos?overlay=schedule_visit" replace />} />
              <Route path="/admin/realtime" element={<Navigate to="/hos?view=realtime" replace />} />
              <Route path="/admin/predictive" element={<Navigate to="/hos?view=predictive" replace />} />
              <Route path="/admin/quality" element={<Navigate to="/hos?view=quality" replace />} />
              <Route path="/admin/hipaa" element={<Navigate to="/hos?view=hipaa" replace />} />
              <Route path="/admin/video-analytics" element={<Navigate to="/hos?view=video-analytics" replace />} />
              <Route path="/command-center" element={<Navigate to="/hos" replace />} />
              <Route path="/family-hub" element={<FamilyHub />} />
              <Route path="/patient-outcomes" element={<Navigate to="/hos?view=outcomes" replace />} />
              <Route path="/hospice-ehr" element={<Navigate to="/hos" replace />} />
              <Route path="/hospice-ehr/voice-documentation" element={<Navigate to="/hos?overlay=voice_documentation" replace />} />
              <Route path="/hospice-ehr/clinical-intelligence" element={<Navigate to="/hos?view=clinical-intelligence" replace />} />
              <Route path="/hospice-ehr/command-center" element={<Navigate to="/hos" replace />} />
              <Route path="/hospice-ehr/documents" element={<Navigate to="/hos?overlay=documents" replace />} />
              <Route path="/hospice-ehr/referral-to-emr" element={<Navigate to="/hos?view=referral-intake" replace />} />
              <Route path="/hospice-ehr/compliance" element={<Navigate to="/hos?overlay=compliance" replace />} />
              <Route path="/hospice-ehr/medications" element={<Navigate to="/hos?overlay=mar" replace />} />
              <Route path="/hospice-ehr/analytics" element={<Navigate to="/hos?view=analytics" replace />} />
              <Route path="/ehr-compliance" element={<Navigate to="/hos?overlay=compliance" replace />} />
              <Route path="/ehr-referral-intake" element={<Navigate to="/hos?view=referral-intake" replace />} />
              <Route path="/ehr-orders-medications" element={<Navigate to="/hos?overlay=mar" replace />} />
              <Route path="/ehr-idg-meeting" element={<Navigate to="/hos?overlay=idg_review" replace />} />
              <Route path="/ehr-discharge-revocation" element={<EHRDischargeRevocationPage />} />
              <Route path="/discharge-workflow" element={<EHRDischargeRevocationPage />} />
              <Route path="/ehr-staff-visits" element={<Navigate to="/hos?overlay=visit_note" replace />} />
              <Route path="/ehr-ai-care-plans" element={<AICarePlansPage />} />
              <Route path="/ai-care-plans" element={<AICarePlansPage />} />
              <Route path="/ehr-documentation-qa" element={<Navigate to="/hos?view=documentation-qa" replace />} />
              <Route path="/ehr-cti-grader" element={<Navigate to="/hos?overlay=recertification" replace />} />
              <Route path="/ehr-ai-scheduling" element={<AISchedulingPage />} />
              <Route path="/ehr-oms-automation" element={<OMSAutomationPage />} />
              <Route path="/ehr-fall-automation" element={<FallAutomationPage />} />
              <Route path="/ehr-qapi-falls" element={<QAPIFallLogPage />} />
              <Route path="/ehr-predictive-falls" element={<PredictiveFallsPage />} />
              <Route path="/ehr-skin-automation" element={<SkinAutomationPage />} />
              <Route path="/ehr-respiratory-crisis" element={<RespiratoryCrisisPage />} />
              <Route path="/family-peace-of-mind" element={<FamilyPortalPage />} />
              <Route path="/idg-summary" element={<IDGSummaryPage />} />
              
              {/* Protected HOS Routes - Requires Staff or Admin */}
              <Route path="/hos" element={
                <ProtectedRoute allowedRoles={["admin", "staff"]}>
                  <HospiceOSPage />
                </ProtectedRoute>
              } />
              <Route path="/hos/:patientId" element={
                <ProtectedRoute allowedRoles={["admin", "staff"]}>
                  <HospiceOSPage />
                </ProtectedRoute>
              } />
              
              {/* Clinical Automation Dashboard (Master System Role) */}
              <Route path="/clinical-automation" element={
                <ProtectedRoute allowedRoles={["admin", "staff"]}>
                  <ClinicalAutomationDashboard />
                </ProtectedRoute>
              } />
              
              {/* Compliance Hub (P0 Critical - HOPE, F2F, Med Rec, Teach-back) */}
              <Route path="/compliance-hub" element={
                <ProtectedRoute allowedRoles={["admin", "staff"]}>
                  <ComplianceHub />
                </ProtectedRoute>
              } />
              
              {/* LCD Eligibility Checker (CMS Hospice Eligibility Evaluation) */}
              <Route path="/lcd-eligibility" element={
                <ProtectedRoute allowedRoles={["admin", "staff", "physician"]}>
                  <LCDEligibilityChecker />
                </ProtectedRoute>
              } />
              
              {/* EHR Interoperability Configuration (Admin Only) */}
              <Route path="/ehr-config" element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <EHRInteropConfig />
                </ProtectedRoute>
              } />
              
              {/* Volunteer Management (Full HOS Integration) */}
              <Route path="/volunteer-management" element={
                <ProtectedRoute allowedRoles={["admin", "staff"]}>
                  <VolunteerManagement />
                </ProtectedRoute>
              } />
              
              {/* RN Command Center (Comprehensive RN Dashboard with Voice Documentation) */}
              <Route path="/rn-command-center" element={
                <ProtectedRoute allowedRoles={["admin", "staff"]}>
                  <RNCommandCenter />
                </ProtectedRoute>
              } />
              
              {/* HOPE Demographics Page (CMS Compliance) */}
              <Route path="/hope-demographics" element={
                <ProtectedRoute allowedRoles={["admin", "staff"]}>
                  <HOPEDemographicsPage />
                </ProtectedRoute>
              } />
              
              {/* Physician CTI - Redirect to Command Center */}
              <Route path="/physician-cti" element={
                <ProtectedRoute allowedRoles={["admin", "physician", "doctor", "medical_director"]}>
                  <Navigate to="/physician-command-center" replace />
                </ProtectedRoute>
              } />
              
              {/* Admin Settings Page (Scheduled Reports) */}
              <Route path="/admin-settings" element={
                <ProtectedRoute allowedRoles={["admin", "administrator"]}>
                  <AdminSettingsPage />
                </ProtectedRoute>
              } />
              
              {/* Legacy RN Voice Command - redirects to Command Center */}
              <Route path="/rn-voice-command" element={
                <Navigate to="/rn-command-center" replace />
              } />
              
              <Route path="/staff-tools" element={<StaffToolsPage />} />
              <Route path="/visit-frequency" element={<VisitFrequencyPage />} />
              <Route path="/patient-portal" element={<PatientPortalPage />} />
              <Route path="/notification-center" element={<NotificationCenterPage />} />
              <Route path="/email-analytics" element={<EmailAnalyticsPage />} />
              <Route path="/translation-export" element={<TranslationExportPage />} />
              <Route path="/hospice-ehr/transitions" element={<Navigate to="/hos?view=transitions" replace />} />
              <Route path="/hospice-ehr/volunteer" element={<Navigate to="/hos?view=volunteer" replace />} />
              <Route path="/hospice-ehr/scheduling" element={<Navigate to="/hos?overlay=schedule_visit" replace />} />
              <Route path="/hospice-ehr/interoperability" element={<Navigate to="/hos?view=interoperability" replace />} />
              <Route path="/hospice-ehr/regulatory" element={<Navigate to="/hos?view=regulatory" replace />} />
              <Route path="/hospice-ehr/rcm" element={<Navigate to="/hos?view=rcm" replace />} />
              <Route path="/hospice-ehr/operations" element={<Navigate to="/hos?view=operations" replace />} />
              <Route path="/hospice-ehr/claims" element={<Navigate to="/hos?view=claims" replace />} />
              <Route path="/hospice-ehr/peer-review" element={<Navigate to="/hos?view=peer-review" replace />} />
              <Route path="/hospice-ehr/financial-reports" element={<Navigate to="/hos?view=financial" replace />} />
              <Route path="/comprehensive-reports" element={<Navigate to="/hos?view=reports" replace />} />
              <Route path="/hospice-ehr/comprehensive-reports" element={<Navigate to="/hos?view=reports" replace />} />
              <Route path="/video-calls" element={<VideoCallCenter />} />
              <Route path="/team-chat" element={<ChatCenter />} />
              <Route path="/ai-analytics" element={<Navigate to="/hos?view=analytics" replace />} />
              {/* New Strategic Enhancement Suites */}
              <Route path="/clinical-ai" element={<Navigate to="/hos?view=clinical-ai" replace />} />
              <Route path="/operations-suite" element={<Navigate to="/hos?view=operations" replace />} />
              <Route path="/performance-bi" element={<Navigate to="/hos?view=performance" replace />} />
              <Route path="/integrations" element={<IntegrationsDashboard />} />
              {/* New Features - Hotlist, Visit Requests, Custom Alerts */}
              <Route path="/hotlist" element={<Navigate to="/hos?view=hotlist" replace />} />
              <Route path="/visit-requests" element={<Navigate to="/hos?view=visit-requests" replace />} />
              <Route path="/custom-alerts" element={<CustomAlertsDashboard />} />
              <Route path="/family-portal-enhanced" element={<FamilyPortalDashboard />} />
              <Route path="/sbar-tool" element={<SBARPromptTool />} />
              <Route path="/ai-eprescription" element={<AIEPrescriptionDashboard />} />
              <Route path="/referral-admission" element={<ReferralAdmissionDashboard />} />
              <Route path="/admissions" element={<AdmissionsDashboard />} />
              <Route path="/admissions-workflow" element={<AdmissionsDashboard />} />
              <Route path="/referral-portal" element={<IntegratedReferralPortal />} />
              <Route path="/referrals" element={<IntegratedReferralPortal />} />
              <Route path="/physician-messaging" element={<PhysicianMessagingCenter />} />
              <Route path="/dme-ordering" element={<DMESuppliesOrdering />} />
              <Route path="/staff-supplies" element={<StaffSuppliesPortal />} />
              <Route path="/physician-ai-dashboard" element={<Navigate to="/physician-command-center" replace />} />
              <Route path="/physician-dashboard-enhanced" element={<Navigate to="/physician-command-center" replace />} />
              <Route path="/rncm-dashboard" element={<Navigate to="/rn-command-center" replace />} />
              <Route path="/rn-ai-dashboard" element={<RNAIDashboard />} />
              <Route path="/admission-workflow" element={<AdmissionWorkflowTracker />} />
              <Route path="/escalations" element={<EscalationsDashboard />} />
              <Route path="/hope-dashboard" element={<HOPEDashboard />} />
              <Route path="/family-symptoms" element={<FamilySymptomPortal />} />
              <Route path="/idg-meeting" element={<IDGLiveMeeting />} />
              
              {/* Phase 38-44 Comprehensive Platform Routes */}
              <Route path="/caregiver-command-center" element={<CaregiverCommandCenter />} />
              <Route path="/collaborating-physician" element={<CollaboratingPhysicianPortal />} />
              <Route path="/advanced-telehealth" element={<AdvancedTelehealthInterface />} />
              <Route path="/stability-score" element={<StabilityScoreDashboard />} />
              <Route path="/facility-portal" element={<FacilityTransparencyPortal />} />
              <Route path="/legacy-bereavement" element={<LegacyBereavementModule />} />
              <Route path="/smart-triage" element={<SmartTriageDashboard />} />
              
              {/* Phase 45 Additional Enhancement Routes */}
              <Route path="/caregiver-analytics" element={<CaregiverConfidenceAnalytics />} />
              <Route path="/resource-library" element={<ResourceLibrary />} />
              <Route path="/business-documents" element={<BusinessDocumentsPage />} />
              <Route path="/ai-hub" element={<Wave1FeaturesPage />} />
              <Route path="/digital-legacy" element={<Wave2FeaturesPage />} />
              <Route path="/operations-intelligence" element={<Wave3FeaturesPage />} />
              <Route path="/advanced-experience" element={<Wave4FeaturesPage />} />
              <Route path="/ambient-documentation" element={<AmbientDocumentationPage />} />
              <Route path="/command-intelligence" element={<IntelligenceDashboardPage />} />
              <Route path="/video-visit/:code" element={<VideoVisitPage />} />
              <Route path="/patient-chart" element={<PatientChartPage />} />
              <Route path="/workflow-billing" element={<WorkflowBillingPage />} />
              <Route path="/family-portal" element={<FamilyPortalPage />} />
              <Route path="/ai-scribe" element={<AIScribe />} />
              <Route path="/unified-timeline" element={<UnifiedTimeline />} />
              <Route path="/smart-orders" element={<SmartOrderSet />} />
              <Route path="/geofencing" element={<GeofencingDashboard />} />
              
              {/* Phase 46 Peer Support & Offline Routes */}
              <Route path="/caregiver-peer-support" element={<CaregiverPeerSupport />} />
              <Route path="/medication-reminders" element={<MedicationReminders />} />
              <Route path="/care-plan-export" element={<CarePlanExport />} />
              <Route path="/legacy-video-messages" element={<LegacyVideoMessages />} />
              
              {/* Phase 47 PWA, Scheduling, Executive Tools Routes */}
              <Route path="/schedule-appointment" element={<AppointmentScheduler />} />
              <Route path="/symptom-diary" element={<SymptomDiaryEnhanced />} />
              <Route path="/executive-dashboard" element={<ExecutiveDashboardEnhanced />} />
              <Route path="/incident-reporting" element={<IncidentReporting />} />
              
              {/* Phase 48 Grief & Bereavement Support Routes */}
              <Route path="/grief-support" element={<GriefSupport />} />
              
              {/* Phase 49 Virtual Tours & Advanced Planning Routes */}
              <Route path="/virtual-tours" element={<VirtualTours />} />
              <Route path="/benchmarks" element={<BenchmarkAnalytics />} />
              
              {/* Phase 50 Workforce Management Routes - Admin Only */}
              <Route path="/workforce" element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <WorkforceManagement />
                </ProtectedRoute>
              } />
              
              {/* Operations Dashboard - Admin Only */}
              <Route path="/operations" element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <OperationsDashboard />
                </ProtectedRoute>
              } />
              
              {/* Real-Time Operations - Admin Only */}
              <Route path="/realtime-operations" element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <RealTimeOperationsDashboard />
                </ProtectedRoute>
              } />
              
              {/* Phase 51 Volunteer Management Routes - Legacy route kept for backwards compatibility */}
              <Route path="/volunteers" element={<VolunteerManagementLegacy />} />
              
              {/* Volunteer & Bereavement Management */}
              <Route path="/volunteer-bereavement" element={<VolunteerBereavement />} />
              
              {/* Phase 52 GPS & Smart Watch Routes */}
              <Route path="/gps-smartwatch" element={<GPSSmartWatch />} />
              
              {/* Phase 53 Admin Tools Routes */}
              <Route path="/admin-tools" element={<RolePermissionBuilder />} />
              
              {/* Phase 54 Monitoring Routes */}
              <Route path="/monitoring" element={<AuditTrailExplorer />} />
              
              {/* Phase 55 Video Conferencing Routes */}
              <Route path="/video-conference" element={<VideoConference />} />
              
              {/* Phase 56 Billing & RCM Routes */}
              <Route path="/billing-rcm" element={<BillingRCM />} />
              
              {/* Phase 57 DME & Pharmacy Enhanced Routes */}
              <Route path="/dme-pharmacy" element={<DMEPharmacyDashboard />} />
              
              {/* Phase 58 Quality & Compliance Routes */}
              <Route path="/quality-compliance" element={<QualityComplianceDashboard />} />
              
              {/* Phase 59 Real-Time Notifications Routes */}
              <Route path="/notification-center" element={<NotificationCenter />} />
              
              {/* Phase 60 Medication Interaction Checker Routes */}
              <Route path="/medication-interactions" element={<MedicationInteractionChecker />} />
              
              {/* AI-Automated Medication Workflow Routes */}
              <Route path="/medication-workflow" element={<MedicationWorkflowPage />} />
              
              {/* Phase 61 Auto-Reorder Routes */}
              <Route path="/auto-reorder" element={<AutoReorderDashboard />} />
              
              {/* Phase 62 Bereavement Support Routes */}
              <Route path="/bereavement-support" element={<BereavementSupportDashboard />} />
              
              {/* Phase 63 Patient Acuity Scoring Routes */}
              <Route path="/patient-acuity" element={<PatientAcuityDashboard />} />
              <Route path="/acuity-trends" element={<PatientAcuityTrendsDashboard />} />
              <Route path="/predictive-alerts" element={<PredictiveEscalationAlerts />} />
              
              {/* Phase 64 Voice-to-Documentation Routes */}
              <Route path="/voice-documentation" element={<VoiceDocumentationDashboard />} />
              
              {/* Phase 65 Smart Scheduling Routes */}
              <Route path="/smart-scheduling" element={<SmartSchedulingDashboard />} />
              
              {/* Phase 66 Predictive Escalation Routes */}
              <Route path="/predictive-escalation" element={<PredictiveEscalationDashboard />} />
              
              {/* Phase 67 Family Portal Enhanced Routes */}
              <Route path="/family-messaging" element={<FamilyPortalEnhanced />} />
              
              {/* Phase 68 Referral Source Portal Routes */}
              <Route path="/external-referrals" element={<ReferralSourcePortal />} />
              
              {/* Phase 69 Staff Performance Analytics Routes */}
              <Route path="/staff-performance" element={<StaffPerformanceDashboard />} />
              
              {/* Phase 70 Family Caregiver Mobile App (PWA) Routes */}
              <Route path="/caregiver-app" element={<CaregiverMobileApp />} />
              
              {/* Phase 71 VR Comfort Experiences Routes */}
              <Route path="/vr-comfort" element={<VRComfortDashboard />} />
              
              {/* Phase 72 AI Grief Counselor Routes */}
              <Route path="/ai-grief-counselor" element={<GriefCounselorChat />} />
              
              {/* Phase 73 Daily Wellness Check-in Routes */}
              <Route path="/wellness-checkin" element={<WellnessCheckin />} />
              
              {/* Phase 74 Memory & Legacy Journal Routes */}
              <Route path="/memory-journal" element={<MemoryJournal />} />
              
              {/* Phase 75 Enhanced Caregiver Features Routes */}
              <Route path="/caregiver-dashboard" element={<CaregiverDashboard />} />
              <Route path="/local-resources" element={<LocalResourceFilter />} />
              
              {/* Phase 76 Analytics Routes */}
              <Route path="/analytics" element={<AdvancedAnalyticsDashboard />} />
              <Route path="/reports" element={<AdvancedAnalyticsDashboard />} />
              
              {/* Phase 77 Integration Marketplace Routes */}
              <Route path="/integration-marketplace" element={<IntegrationMarketplace />} />
              <Route path="/marketplace" element={<IntegrationMarketplace />} />
              
              {/* Phase 78 Push Notifications & Registration Routes */}
              <Route path="/push-notifications" element={<PushNotificationManager />} />
              <Route path="/join" element={<CombinedRegistration />} />
              
              {/* Phase 79 Family Photo Gallery Routes */}
              <Route path="/photo-gallery" element={<FamilyPhotoGallery />} />
              <Route path="/photos" element={<FamilyPhotoGallery />} />
              
              {/* Note: /wellness route is defined in Phase 26 section above pointing to StaffWellness */}
              <Route path="/certificates" element={<CEUCertificates />} />
              {/* Registration Routes */}
              <Route path="/partner-register" element={<PartnerRegistration />} />
              <Route path="/register" element={<PatientFamilyRegistration />} />
              <Route path="/partner-dashboard" element={<PartnerDashboard />} />
              
              {/* ==================================================================
                  HOS MIGRATION REDIRECTS
                  Redirect legacy routes to consolidated /hos environment
                  ================================================================== */}
              
              {/* Admin routes redirect to /hos */}
              <Route path="/admin/activities" element={<Navigate to="/hos?view=activities" replace />} />
              <Route path="/admin/triage" element={<Navigate to="/hos?view=triage" replace />} />
              <Route path="/admin/ehr" element={<Navigate to="/hos?view=ehr-dashboard" replace />} />
              <Route path="/admin/ai-features" element={<Navigate to="/hos?view=ai-features" replace />} />
              <Route path="/admin/clinical-intelligence" element={<Navigate to="/hos?view=clinical-intelligence" replace />} />
              <Route path="/admin/analytics" element={<Navigate to="/hos?view=analytics" replace />} />
              <Route path="/admin/scheduling" element={<Navigate to="/hos?view=scheduling" replace />} />
              <Route path="/admin/realtime" element={<Navigate to="/hos?view=realtime-ops" replace />} />
              <Route path="/admin/predictive" element={<Navigate to="/hos?view=predictive-analytics" replace />} />
              <Route path="/admin/quality" element={<Navigate to="/hos?view=quality-metrics" replace />} />
              <Route path="/admin/hipaa" element={<Navigate to="/hos?view=hipaa" replace />} />
              <Route path="/admin/video-analytics" element={<Navigate to="/hos?view=video-analytics" replace />} />
              
              {/* Hospice EHR routes redirect to /hos */}
              <Route path="/hospice-ehr/voice-documentation" element={<Navigate to="/hos?overlay=voice_documentation" replace />} />
              <Route path="/hospice-ehr/clinical-intelligence" element={<Navigate to="/hos?overlay=clinical_intelligence" replace />} />
              <Route path="/hospice-ehr/command-center" element={<Navigate to="/hos" replace />} />
              <Route path="/hospice-ehr/documents" element={<Navigate to="/hos?overlay=documents" replace />} />
              <Route path="/hospice-ehr/referral-to-emr" element={<Navigate to="/hos?view=referral-to-emr" replace />} />
              <Route path="/hospice-ehr/compliance" element={<Navigate to="/hos?overlay=compliance" replace />} />
              <Route path="/hospice-ehr/medications" element={<Navigate to="/hos?overlay=mar" replace />} />
              <Route path="/hospice-ehr/analytics" element={<Navigate to="/hos?view=analytics" replace />} />
              <Route path="/hospice-ehr/transitions" element={<Navigate to="/hos?view=transitions" replace />} />
              <Route path="/hospice-ehr/volunteer" element={<Navigate to="/hos?view=volunteer" replace />} />
              <Route path="/hospice-ehr/scheduling" element={<Navigate to="/hos?overlay=schedule_visit" replace />} />
              <Route path="/hospice-ehr/interoperability" element={<Navigate to="/hos?view=interoperability" replace />} />
              <Route path="/hospice-ehr/regulatory" element={<Navigate to="/hos?view=regulatory" replace />} />
              <Route path="/hospice-ehr/rcm" element={<Navigate to="/hos?view=rcm" replace />} />
              <Route path="/hospice-ehr/operations" element={<Navigate to="/hos?view=operations" replace />} />
              <Route path="/hospice-ehr/claims" element={<Navigate to="/hos?view=claims" replace />} />
              <Route path="/hospice-ehr/peer-review" element={<Navigate to="/hos?view=peer-review" replace />} />
              <Route path="/hospice-ehr/financial-reports" element={<Navigate to="/hos?view=financial-reports" replace />} />
              <Route path="/hospice-ehr/comprehensive-reports" element={<Navigate to="/hos?view=reports" replace />} />
              <Route path="/hospice-ehr" element={<Navigate to="/hos" replace />} />
              
              {/* EHR feature routes redirect to /hos */}
              <Route path="/ehr-compliance" element={<Navigate to="/hos?overlay=compliance" replace />} />
              <Route path="/ehr-referral-intake" element={<Navigate to="/hos?view=referral-intake" replace />} />
              <Route path="/ehr-orders-medications" element={<Navigate to="/hos?overlay=mar" replace />} />
              <Route path="/ehr-idg-meeting" element={<Navigate to="/hos?overlay=idg_review" replace />} />
              <Route path="/ehr-discharge-revocation" element={<Navigate to="/hos?view=discharge" replace />} />
              <Route path="/ehr-staff-visits" element={<Navigate to="/hos?overlay=visit_note" replace />} />
              <Route path="/ehr-ai-care-plans" element={<AICarePlansPage />} />
              <Route path="/ai-care-plans" element={<AICarePlansPage />} />
              <Route path="/ehr-documentation-qa" element={<Navigate to="/hos?view=documentation-qa" replace />} />
              <Route path="/ehr-cti-grader" element={<Navigate to="/hos?overlay=recertification" replace />} />
              <Route path="/ehr-ai-scheduling" element={<Navigate to="/hos?overlay=schedule_visit" replace />} />
              
              {/* Other hospice routes redirect to /hos */}
              <Route path="/hospice-eligibility" element={<Navigate to="/hos?view=eligibility" replace />} />
              <Route path="/care-calendar" element={<Navigate to="/hos?overlay=care_calendar" replace />} />
              <Route path="/command-center" element={<Navigate to="/hos" replace />} />
              <Route path="/patient-outcomes" element={<Navigate to="/hos?view=outcomes" replace />} />
              <Route path="/hope-dashboard" element={<Navigate to="/hos?overlay=hope_assessment" replace />} />
              
              {/* 404 Catch-All Route - Must be last */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>
          <Footer />
          <Toaster position="top-right" />
          <GlobalVoiceCommands />
          <PremiumVoiceAssistant />
          <PremiumTranslator />
        </BrowserRouter>
      </div>
            </FocusModeProvider>
      </PatientContextProvider>
      </PresenceProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
